import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { createAdyenComponent } from '@/utils/adyen';

import { useApplePayConfiguration } from '@/components/ApplePayExpressButton/hooks/useApplePayConfiguration';
import { useApplePayExpressPaymentFlow } from '@/components/ApplePayExpressButton/hooks/useApplePayExpressPaymentFlow';
import { useApplePayTermsConsent } from '@/components/ApplePayExpressButton/hooks/useApplePayTermsConsent';
import { useCheckout } from '@/hooks/useCheckout';
import { getAmountInMinorUnits } from '@/lib/amount';
import { createAdyenCheckout } from '@/lib/payment/helpers';
import { PAYMENT_TYPES } from '@/lib/payment/types';
import type { ApiError, ApiErrorType } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutState } from '@/states/checkout';

interface UseApplePayExpressButtonProps {
  areBraceletSizeErrorsSet: () => boolean;
  errorHandler?: (
    errors: ApiError[],
    opts?: { errorType?: ApiErrorType },
  ) => void;
}

export const useApplePayExpressCheckout = ({
  areBraceletSizeErrorsSet,
  errorHandler,
}: UseApplePayExpressButtonProps) => {
  const { formatMessage } = useIntl();
  const { country, locale } = useRegionProvider();
  const {
    snapshot: {
      checkout: { totalPrice },
      preOrderData,
    },
  } = useCheckout<true>();
  const [isAvailable, setIsAvailable] = useState<boolean>(false); // Whether Apple Pay is available in the current browser environment
  const buttonRef = useRef<HTMLDivElement>(null);
  const affiliateOptInRef = useRef<boolean>(false);
  const isDepositTransaction = !!preOrderData?.deposit;

  const getTransactionAmount = (): number => {
    let amount;
    if (!isDepositTransaction) {
      amount = checkoutState.checkout?.totalPrice?.gross.amount;
    } else {
      amount = checkoutState.preOrderData?.deposit;
    }

    return amount ?? 0;
  };

  const {
    gatewayConfigs,
    gatewayId,
    isActivated,
    isExpressPaymentAllowed,
    isGatewayConfigLoading,
  } = useApplePayConfiguration();

  const initializeApplePay = useCallback(async () => {
    if (!buttonRef.current || !isActivated || !gatewayId) return;

    try {
      // Create Adyen checkout instance
      const adyenCheckout = await createAdyenCheckout({
        showPayButton: true,
        countryCode: country.code,
        price: {
          ...totalPrice.gross,
          amount: getAmountInMinorUnits(
            getTransactionAmount(),
            totalPrice.gross.currency,
          ),
        },
        onSubmit: handleSubmit(gatewayId),
        onError: handleError,
        locale: locale.locale,
        formatMessage,
        paymentMethodType: PAYMENT_TYPES.APPLE_PAY_EXPRESS,
      });

      // Get Apple Pay drop-in configuration
      const appleDropinConfiguration = gatewayConfigs
        .find((item) => item.id === gatewayId)
        ?.paymentMethods.find(
          (item) => item.type === PAYMENT_TYPES.APPLE_PAY_EXPRESS,
        )?.dropInConfiguration;

      // Create Apple Pay component
      const appleComponent = createAdyenComponent(
        PAYMENT_TYPES.APPLE_PAY_EXPRESS,
        adyenCheckout,
        {
          isExpress: true,
          userAction: 'continue',
          onClick: handleClick,
          onShippingContactSelected: handleShippingAddressChange,
          onAuthorized: handleAuthorized,
          requiredBillingContactFields: [
            'postalAddress',
            'name',
            'phoneticName',
            'phone',
            'email',
          ],
          requiredShippingContactFields: [
            'postalAddress',
            'name',
            'phoneticName',
            'phone',
            'email',
          ],
          intent: 'authorize',
          ...appleDropinConfiguration,
          buttonColor: 'white-outline',
        },
      );

      await appleComponent.isAvailable();
      setIsAvailable(true);

      // Mount the component
      if (buttonRef.current) {
        appleComponent.mount(buttonRef.current);
      }
    } catch (error) {
      setIsAvailable(false);
      console.error('Failed to initialize Apple Pay:', error);
    }
  }, [buttonRef.current, isActivated, gatewayId]);

  const {
    handleAuthorized,
    handleError,
    handleShippingAddressChange,
    handleSubmit,
  } = useApplePayExpressPaymentFlow({
    affiliateOptInRef,
    errorHandler,
    initializeApplePay,
  });

  const {
    handleClick,
    isTermsOpen,
    onCancelConsent,
    onConfirmConsent,
    showNflMarketingOption,
  } = useApplePayTermsConsent({
    affiliateOptInRef,
    areBraceletSizeErrorsSet,
  });

  useEffect(() => {
    initializeApplePay();
  }, [initializeApplePay]);

  return {
    buttonRef,
    isActivated,
    isAvailable,
    isExpressPaymentAllowed,
    isGatewayConfigLoading,
    isTermsOpen,
    onCancelConsent,
    onConfirmConsent,
    showNflMarketingOption,
  };
};
